import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { BATHROOM_HOTSPOTS } from '../../data/content';
import { Icons } from '../ui/Icons';
import { Reveal } from '../ui/Reveal';

const easeOut = [0.22, 1, 0.36, 1] as const;

export function InteractiveBathroom() {
  const { openLeadForm } = useApp();
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  const total = BATHROOM_HOTSPOTS.length;
  const hotspot = BATHROOM_HOTSPOTS[active];
  const slide = reduce ? 0 : 16;

  const go = (dir: number) => setActive((i) => (i + dir + total) % total);

  return (
    <section id="explore" className="relative bg-mist py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-60" />
      <div className="pointer-events-none absolute -right-24 top-24 h-80 w-80 rounded-full bg-terracotta/8 blur-3xl" />

      <div className="container-rosa relative">
        {/* Header */}
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Explore the fixes</p>
          <h2 className="mt-3 font-display text-[2.1rem] font-bold leading-[1.08] tracking-[-0.01em] text-plum sm:text-5xl">
            See what makes a bathroom safe.
          </h2>
          <p className="mx-auto mt-5 max-w-xl font-body text-lg leading-relaxed text-plum/70">
            Most senior falls happen in the bathroom, yet small, well-placed changes make it safe
            again. Tap or hover a marker to see what each fix does.
          </p>
        </Reveal>

        {/* Widget */}
        <div className="mt-12 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* LEFT — image with markers */}
          <Reveal>
            <div className="relative overflow-hidden rounded-[1.5rem] border-4 border-white shadow-lift">
              <img
                src="/images/bathroom-safe.webp"
                alt="A warm, safe bathroom with a curbless walk-in shower and grab bars"
                className="aspect-[4/3] w-full object-cover"
                width={800}
                height={600}
                loading="lazy"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-plum/15 via-transparent to-transparent" />

              {BATHROOM_HOTSPOTS.map((h, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={h.id}
                    type="button"
                    aria-label={h.title}
                    aria-pressed={isActive}
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    style={{ left: `${h.x}%`, top: `${h.y}%` }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                  >
                    <span className="relative flex h-7 w-7 items-center justify-center">
                      {isActive && (
                        <span className="absolute inset-0 animate-pulse-ring rounded-full bg-terracotta/40" />
                      )}
                      <span
                        className={`relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-terracotta text-blush shadow-lift transition-transform duration-300 ${
                          isActive ? 'scale-110' : 'hover:scale-105'
                        }`}
                      >
                        <span className="font-body text-[13px] font-bold leading-none">{i + 1}</span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>

          {/* RIGHT — detail panel */}
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col rounded-card border border-stone bg-white p-6 shadow-soft">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-terracotta/12 text-terracotta">
                  <Icons.sparkle width={22} height={22} />
                </span>
                <span className="font-body text-sm font-semibold uppercase tracking-[0.18em] text-plum/45">
                  Fix {active + 1} of {total}
                </span>
              </div>

              <div className="relative mt-5 min-h-[132px] flex-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={hotspot.id}
                    initial={{ opacity: 0, x: slide, y: slide / 2 }}
                    animate={{ opacity: 1, x: 0, y: 0 }}
                    exit={{ opacity: 0, x: -slide, y: 0 }}
                    transition={{ duration: 0.4, ease: easeOut }}
                  >
                    <h3 className="font-display text-xl font-semibold text-plum">{hotspot.title}</h3>
                    <p className="mt-2.5 font-body leading-relaxed text-plum/70">{hotspot.body}</p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Pager */}
              <div className="mt-6 flex items-center justify-between border-t border-stone pt-5">
                <div className="flex items-center gap-2">
                  {BATHROOM_HOTSPOTS.map((h, i) => (
                    <button
                      key={h.id}
                      type="button"
                      aria-label={`Show ${h.title}`}
                      onClick={() => setActive(i)}
                      className={`h-2 cursor-pointer rounded-full transition-all duration-300 ${
                        i === active ? 'w-6 bg-terracotta' : 'w-2 bg-stone hover:bg-terracotta/40'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Previous fix"
                    onClick={() => go(-1)}
                    className="btn-secondary !px-3 !py-3"
                  >
                    <Icons.arrowLeft width={18} height={18} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next fix"
                    onClick={() => go(1)}
                    className="btn-secondary !px-3 !py-3"
                  >
                    <Icons.arrowRight width={18} height={18} />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Under-widget CTA */}
        <Reveal delay={0.15} className="mt-12 flex flex-col items-center gap-5 text-center">
          <p className="max-w-xl font-body text-lg text-plum/70">
            Not sure which your home needs? The free assessment will tell you.
          </p>
          <button type="button" onClick={() => openLeadForm()} className="btn-primary">
            Book my free assessment
            <Icons.arrowRight width={18} height={18} />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
